import React, { useState, useEffect, useCallback } from "react";
import {
  Input,
  Button,
  Space,
  Popconfirm,
  Tag,
  message,
  Switch,
  Tooltip,
  Drawer,
  Row,
  Col,
  Card,
  Select
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
  AppstoreOutlined
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import LifestyleServ from "../../service/LifestyleServ";
import Breadcrumbs from "../../component/Breadcrumbs";
import DataTable from "../../component/ui/DataTable";
import SafeImage, { getImageUrl } from "../../component/SafeImage";
import { ResetButton } from "../../component/ui/ActionButtons";

const { Option } = Select;

const LifestyleManager = () => {
  const navigate = useNavigate();
  const [lifestyles, setLifestyles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [selectedType, setSelectedType] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerRecord, setDrawerRecord] = useState(null);

  // Fetch Lifestyles data
  const fetchLifestyles = useCallback(async (p = page, search = searchText, type = selectedType, status = selectedStatus, size = pageSize) => {
    try {
      setLoading(true);
      const params = {
        isAdmin: true,
        limit: size,
        skip: (p - 1) * size,
        sort: JSON.stringify({ updatedAt: -1, createdAt: -1 }),
      };

      if (search && search.trim()) {
        params.search = search.trim();
      }

      if (type && type !== "all") {
        params.type = type;
      }

      const res = await LifestyleServ.index(params);
      let list = [];
      let total = 0;

      if (Array.isArray(res?.data)) {
        list = res.data;
        total = res.total || res.data.length;
      } else if (Array.isArray(res)) {
        list = res;
        total = res.length;
      } else if (res?.data?.data && Array.isArray(res.data.data)) {
        list = res.data.data;
        total = res.data.total || list.length;
      }

      // Filter by status on client-side if selected
      if (status !== "all") {
        const isActive = status === "active";
        list = list.filter(item => (item.isActive !== false && item.isLive !== false) === isActive);
      }

      setLifestyles(list);
      setTotalCount(total);
    } catch (err) {
      console.error("Failed to load lifestyles:", err);
      message.error("Failed to load lifestyle collections");
    } finally {
      setLoading(false);
    }
  }, [page, searchText, selectedType, selectedStatus, pageSize]);

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchLifestyles(page, searchText, selectedType, selectedStatus, pageSize);
    }, 300);
    return () => clearTimeout(handler);
  }, [page, searchText, selectedType, selectedStatus, pageSize, fetchLifestyles]);

  const handleAdd = () => {
    navigate("/admin/lifestyles/add");
  };

  const handleEdit = (record) => {
    const id = record._id || record.id;
    navigate(`/admin/lifestyles/edit/${id}`);
  };

  const handleDelete = async (record) => {
    const id = record._id || record.id;
    try {
      await LifestyleServ.remove(id);
      message.success("Lifestyle & Need collection deleted successfully");
      fetchLifestyles();
      if (drawerRecord && (drawerRecord._id || drawerRecord.id) === id) {
        setDrawerOpen(false);
      }
    } catch (err) {
      message.error(err.message || "Failed to delete collection");
    }
  };

  const handleToggleStatus = async (record, checked, e) => {
    if (e) e.stopPropagation();
    const id = record._id || record.id;
    try {
      await LifestyleServ.update(id, { isActive: checked, isLive: checked });
      message.success(`Collection is now ${checked ? "Active" : "Inactive"}`);
      setLifestyles(prev =>
        prev.map(item =>
          (item._id || item.id) === id ? { ...item, isActive: checked, isLive: checked } : item
        )
      );
    } catch (err) {
      message.error("Failed to update status");
    }
  };

  const handleViewDrawer = (record) => {
    setDrawerRecord(record);
    setDrawerOpen(true);
  };

  const handleResetFilters = () => {
    setSearchText("");
    setSelectedType("all");
    setSelectedStatus("all");
    setPage(1);
  };

  // Metrics summary
  const totalLifestyleCount = lifestyles.filter(l => l.type === "lifestyle" || l.type === "both").length;
  const totalNeedCount = lifestyles.filter(l => l.type === "need" || l.type === "both").length;
  const totalActiveCount = lifestyles.filter(l => l.isActive !== false && l.isLive !== false).length;

  // Table Columns
  const columns = [
    {
      title: "Card Preview",
      key: "preview",
      width: 130,
      align: "center",
      render: (_, record) => {
        const bg = record.bg || "#EFF6FF";
        const titleColor = record.titleColor || "#003366";
        const btnBg = record.btnBg || "#0055FF";
        const borderColor = record.borderColor || "#DBEAFE";
        const img = record.imageUrl || record.image;

        return (
          <div
            onClick={() => handleViewDrawer(record)}
            style={{
              width: 100,
              height: 60,
              borderRadius: 8,
              background: bg,
              border: `1.5px solid ${borderColor}`,
              padding: 4,
              position: "relative",
              overflow: "hidden",
              cursor: "pointer",
              boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              margin: "0 auto"
            }}
          >
            <div style={{
              fontSize: 8,
              fontWeight: 800,
              color: titleColor,
              lineHeight: 1.1,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "60%"
            }}>
              {record.title?.replace("\n", " ")}
            </div>

            <div style={{
              fontSize: 7,
              background: btnBg,
              color: "#fff",
              borderRadius: 4,
              padding: "1px 4px",
              alignSelf: "flex-start",
              fontWeight: 700
            }}>
              &gt;
            </div>

            <div style={{
              position: "absolute",
              right: 2,
              bottom: 2,
              width: 32,
              height: 32,
              borderRadius: 4,
              overflow: "hidden",
              background: "#fff"
            }}>
              <SafeImage
                src={getImageUrl(img)}
                alt="thumb"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            </div>
          </div>
        );
      },
    },
    {
      title: "Collection Name",
      dataIndex: "title",
      key: "title",
      render: (title, record) => (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span style={{ fontWeight: 700, color: "#0f172a", fontSize: 13, lineHeight: 1.3 }}>
            {title?.replace("\n", " ")}
          </span>
          {record.subtitle && (
            <span style={{ fontSize: 11, color: "#64748b", marginTop: 2, lineHeight: 1.2 }}>
              {record.subtitle.replace("\n", " ")}
            </span>
          )}
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 3 }}>
            <span style={{ fontSize: 10, color: "#94a3b8" }}>
              ID: {(record._id || record.id)?.toString().slice(-8).toUpperCase()}
            </span>
            {record.updatedAt && (
              <span style={{ fontSize: 10, color: "#6366f1", background: "#eef2ff", padding: "1px 6px", borderRadius: 4, fontWeight: 500 }}>
                🕒 {new Date(record.updatedAt).toLocaleDateString([], { month: "short", day: "numeric" })}{" "}
                {new Date(record.updatedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </span>
            )}
          </div>
        </div>
      ),
    },
    {
      title: "Display Section",
      dataIndex: "type",
      key: "type",
      width: 140,
      align: "center",
      render: (type) => {
        if (type === "lifestyle") {
          return <Tag color="purple" style={{ fontWeight: 700, borderRadius: 12 }}>✨ Lifestyle</Tag>;
        }
        if (type === "need") {
          return <Tag color="green" style={{ fontWeight: 700, borderRadius: 12 }}>🎯 Need</Tag>;
        }
        return <Tag color="orange" style={{ fontWeight: 700, borderRadius: 12 }}>🔥 Both Sections</Tag>;
      },
    },
    {
      title: "Linked Categories",
      key: "categories",
      render: (_, record) => {
        const cats = record.categories || (record.category ? [record.category] : []);
        if (!cats || cats.length === 0) {
          return <Tag color="default" style={{ fontSize: 11 }}>All Categories</Tag>;
        }
        return (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, maxWidth: 220 }}>
            {cats.map((c, idx) => {
              const name = typeof c === "object" ? c.name : c;
              return (
                <Tag key={idx} color="blue" style={{ fontSize: 11, borderRadius: 4, margin: 0 }}>
                  {name}
                </Tag>
              );
            })}
          </div>
        );
      },
    },
    {
      title: "Products",
      key: "products",
      width: 100,
      align: "center",
      render: (_, record) => {
        const prodCount = record.products?.length || 0;
        return (
          <Tooltip title={`${prodCount} curated products linked directly`}>
            <Tag color={prodCount > 0 ? "cyan" : "default"} style={{ fontWeight: 700, borderRadius: 10 }}>
              📦 {prodCount} items
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: "Theme Colors",
      key: "colors",
      width: 120,
      align: "center",
      render: (_, record) => (
        <div style={{ display: "flex", justifyContent: "center", gap: 4 }}>
          <Tooltip title={`Background: ${record.bg || "#EFF6FF"}`}>
            <div style={{ width: 14, height: 14, borderRadius: "50%", background: record.bg || "#EFF6FF", border: "1px solid #ccc" }} />
          </Tooltip>
          <Tooltip title={`Title Color: ${record.titleColor || "#003366"}`}>
            <div style={{ width: 14, height: 14, borderRadius: "50%", background: record.titleColor || "#003366", border: "1px solid #ccc" }} />
          </Tooltip>
          <Tooltip title={`Button Accent: ${record.btnBg || "#0055FF"}`}>
            <div style={{ width: 14, height: 14, borderRadius: "50%", background: record.btnBg || "#0055FF", border: "1px solid #ccc" }} />
          </Tooltip>
          <Tooltip title={`Border: ${record.borderColor || "#DBEAFE"}`}>
            <div style={{ width: 14, height: 14, borderRadius: "50%", background: record.borderColor || "#DBEAFE", border: "1px solid #ccc" }} />
          </Tooltip>
        </div>
      ),
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      width: 80,
      align: "center",
      render: (priority) => (
        <span style={{ fontWeight: 700, color: "#475569", fontSize: 12 }}>
          #{priority || 1}
        </span>
      ),
    },
    {
      title: "Status",
      key: "status",
      width: 120,
      align: "center",
      render: (_, record) => {
        const active = record.isActive !== false && record.isLive !== false;
        return (
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Switch
              size="small"
              checked={active}
              onChange={(checked, e) => handleToggleStatus(record, checked, e)}
              style={{ backgroundColor: active ? "#22c55e" : "#ef4444" }}
            />
            <span style={{
              fontSize: 11,
              fontWeight: 700,
              color: active ? "#16a34a" : "#dc2626",
              textTransform: "uppercase"
            }}>
              {active ? "Active" : "Inactive"}
            </span>
          </div>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 130,
      align: "center",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Preview Details">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined style={{ color: "#0284c7" }} />}
              onClick={() => handleViewDrawer(record)}
            />
          </Tooltip>
          <Tooltip title="Edit Collection">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined style={{ color: "#0055FF" }} />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Delete Collection">
            <Popconfirm
              title="Delete Collection"
              description="Are you sure you want to delete this Lifestyle / Need collection?"
              onConfirm={() => handleDelete(record)}
              okText="Yes, Delete"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
            >
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="lifestyle-manager-container" style={{ padding: "20px 24px", background: "#f8fafc", minHeight: "100vh" }}>
      {/* Header & Breadcrumbs */}
      <div className="pcu-margin-bottom-16">
        <Breadcrumbs
          items={[
            { label: "Admin", link: "/admin/dashboard" },
            { label: "Product Management" },
            { label: "Lifestyle & Needs" },
          ]}
        />
      </div>

      <div style={{ marginBottom: "24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
          <div style={{
            width: "48px",
            height: "48px",
            background: "#fff",
            borderRadius: "12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
            border: "1px solid #f1f5f9",
          }}>
            <span style={{ fontSize: "22px" }}>✨</span>
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: "20px", fontWeight: 800, color: "#1a1d2e", letterSpacing: "-0.5px" }}>
              Lifestyle & Needs Management
            </h1>
            <p style={{ margin: 0, fontSize: "12px", color: "#9ca3af", fontWeight: 500 }}>
              {totalCount} collections managed in system
            </p>
          </div>
        </div>

        <Button
          type="primary"
          onClick={handleAdd}
          style={{
            height: "40px",
            borderRadius: "10px",
            background: "linear-gradient(135deg, #FF8800 0%, #FF5E00 100%)",
            border: "none",
            padding: "0 20px",
            fontSize: "13px",
            fontWeight: 700,
            boxShadow: "0 4px 12px rgba(255,136,0,0.3)",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flexShrink: 0
          }}
        >
          <PlusOutlined style={{ fontSize: "16px" }} /> Add Lifestyle / Need
        </Button>
      </div>

      {/* Summary KPI Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={12} sm={6}>
          <Card bodyStyle={{ padding: "14px 18px" }} style={{ borderRadius: 12, border: "1px solid #e2e8f0" }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#64748b" }}>Total Collections</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#0f172a", marginTop: 4 }}>{totalCount}</div>
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card bodyStyle={{ padding: "14px 18px" }} style={{ borderRadius: 12, border: "1px solid #e2e8f0" }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#8b5cf6" }}>✨ Lifestyle Collections</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#6d28d9", marginTop: 4 }}>{totalLifestyleCount}</div>
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card bodyStyle={{ padding: "14px 18px" }} style={{ borderRadius: 12, border: "1px solid #e2e8f0" }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#10b981" }}>🎯 Need Collections</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#047857", marginTop: 4 }}>{totalNeedCount}</div>
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card bodyStyle={{ padding: "14px 18px" }} style={{ borderRadius: 12, border: "1px solid #e2e8f0" }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#0284c7" }}>🟢 Active Online</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#0369a1", marginTop: 4 }}>{totalActiveCount}</div>
          </Card>
        </Col>
      </Row>

      {/* Filter & Action Bar */}
      <Card
        bodyStyle={{ padding: "16px 20px" }}
        style={{ borderRadius: 12, border: "1px solid #e2e8f0", marginBottom: 20, boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }}
      >
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} sm={10} md={8}>
            <Input
              placeholder="Search collection title or subtitle..."
              prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
              size="middle"
              style={{ borderRadius: 8 }}
            />
          </Col>

          <Col xs={12} sm={6} md={5}>
            <Select
              value={selectedType}
              onChange={(val) => { setSelectedType(val); setPage(1); }}
              style={{ width: "100%" }}
              size="middle"
            >
              <Option value="all">All Sections</Option>
              <Option value="lifestyle">✨ Lifestyle Only</Option>
              <Option value="need">🎯 Need Only</Option>
              <Option value="both">🔥 Both Sections</Option>
            </Select>
          </Col>

          <Col xs={12} sm={6} md={5}>
            <Select
              value={selectedStatus}
              onChange={(val) => { setSelectedStatus(val); setPage(1); }}
              style={{ width: "100%" }}
              size="middle"
            >
              <Option value="all">All Status</Option>
              <Option value="active">Active Only</Option>
              <Option value="inactive">Inactive Only</Option>
            </Select>
          </Col>

          <Col xs={24} sm={24} md={6} style={{ display: "flex", justifyContent: "flex-end" }}>
            <ResetButton
              icon={<ReloadOutlined />}
              text="Reset Filters"
              onClick={handleResetFilters}
            />
          </Col>
        </Row>
      </Card>

      {/* Data Table */}
      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", overflow: "hidden" }}>
        <DataTable
          columns={columns}
          data={lifestyles}
          loading={loading}
          rowKey={(record) => record._id || record.id}
          pagination={{
            current: page,
            pageSize: pageSize,
            total: totalCount,
            onChange: (p, s) => {
              setPage(p);
              setPageSize(s);
            },
          }}
        />
      </div>

      {/* Preview Details Drawer */}
      <Drawer
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <AppstoreOutlined style={{ color: "#0055FF" }} />
            <span>Collection Preview & Details</span>
          </div>
        }
        placement="right"
        width={480}
        onClose={() => setDrawerOpen(false)}
        open={drawerOpen}
      >
        {drawerRecord && (
          <div>
            {/* Live Simulated Card */}
            <div
              style={{
                background: drawerRecord.bg || "#EFF6FF",
                borderRadius: 16,
                border: `1.5px solid ${drawerRecord.borderColor || "#DBEAFE"}`,
                padding: 20,
                position: "relative",
                overflow: "hidden",
                minHeight: 180,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                marginBottom: 20
              }}
            >
              <div style={{ position: "absolute", top: 12, right: 12 }}>
                <Tag color={drawerRecord.type === "lifestyle" ? "purple" : drawerRecord.type === "need" ? "green" : "orange"}>
                  {drawerRecord.type?.toUpperCase()}
                </Tag>
              </div>

              <div style={{ zIndex: 2, maxWidth: "60%" }}>
                <h3 style={{ fontSize: 18, fontWeight: 800, color: drawerRecord.titleColor || "#003366", margin: "0 0 4px 0", lineHeight: 1.2 }}>
                  {drawerRecord.title}
                </h3>
                <p style={{ fontSize: 12, fontWeight: 600, color: drawerRecord.subColor || "#336699", margin: 0 }}>
                  {drawerRecord.subtitle}
                </p>
              </div>

              <div style={{ zIndex: 2, marginTop: 20 }}>
                <button
                  type="button"
                  style={{
                    background: drawerRecord.btnBg || "#0055FF",
                    color: "#fff",
                    border: "none",
                    borderRadius: 20,
                    padding: "6px 16px",
                    fontSize: 12,
                    fontWeight: 700,
                    boxShadow: "0 2px 6px rgba(0,0,0,0.15)"
                  }}
                >
                  Explore &gt;
                </button>
              </div>

              <div
                style={{
                  position: "absolute",
                  right: 12,
                  bottom: 12,
                  width: 90,
                  height: 90,
                  borderRadius: 12,
                  overflow: "hidden",
                  background: "#fff",
                  boxShadow: "0 4px 10px rgba(0,0,0,0.08)"
                }}
              >
                <SafeImage
                  src={getImageUrl(drawerRecord.imageUrl || drawerRecord.image)}
                  alt="drawer-img"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              </div>
            </div>

            {/* Linked Categories Section */}
            <div style={{ marginBottom: 20 }}>
              <h4 style={{ fontSize: 14, fontWeight: 700, color: "#0f172a", marginBottom: 10 }}>
                Linked Categories ({(drawerRecord.categories?.length || 0)})
              </h4>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {(drawerRecord.categories || []).map((cat, idx) => (
                  <Tag key={idx} color="blue" style={{ padding: "4px 10px", borderRadius: 6, fontSize: 12 }}>
                    📁 {typeof cat === "object" ? cat.name : cat}
                  </Tag>
                ))}
              </div>
            </div>

            {/* Curated Products Section */}
            <div style={{ marginBottom: 20 }}>
              <h4 style={{ fontSize: 14, fontWeight: 700, color: "#0f172a", marginBottom: 10 }}>
                Curated Products ({(drawerRecord.products?.length || 0)})
              </h4>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {(drawerRecord.products || []).map((prod, idx) => {
                  const isObj = typeof prod === "object" && prod !== null;
                  const name = isObj ? prod.name : `Product #${String(prod).slice(-6)}`;
                  const price = isObj ? (prod.price || prod.mrp || 0) : null;
                  const img = isObj ? (prod.imageUrl || prod.images?.[0]) : null;
                  return (
                    <div
                      key={idx}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        padding: 10,
                        background: "#f8fafc",
                        borderRadius: 8,
                        border: "1px solid #e2e8f0"
                      }}
                    >
                      <div style={{ width: 40, height: 40, borderRadius: 6, overflow: "hidden", background: "#fff", flexShrink: 0 }}>
                        <SafeImage
                          src={getImageUrl(img)}
                          alt="prod"
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 12, color: "#1e293b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {name}
                        </div>
                        {price !== null && (
                          <div style={{ fontSize: 11, color: "#16a34a", fontWeight: 700 }}>
                            ₹{price}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: "flex", gap: 10, marginTop: 24 }}>
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={() => {
                  setDrawerOpen(false);
                  handleEdit(drawerRecord);
                }}
                style={{ flex: 1 }}
              >
                Edit Collection
              </Button>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default LifestyleManager;
